import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { Outlet, Link, useLocation } from "react-router-dom";

import { TabTitleChevronIcon } from "../icons";
import { routeName } from "../../../utils/app-drawer-utils";

const Chevron = () => (
  <TabTitleChevronIcon
    sx={{
      color: "var(--font-4)",
      width: "1em",
      height: "6px",
      margin: "0 8px",
    }}
  />
);

const toCamelCase = (str = "") =>
  String(str).replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());

const decodeSegments = (pathname) =>
  String(pathname)
    .split("/")
    .filter(Boolean)
    .map((s) => {
      try {
        return decodeURIComponent(s);
      } catch {
        return s;
      }
    });

const getRouteTitleFor = (seg) => {
  if (!seg) return null;
  return routeName[seg] || routeName[toCamelCase(seg)] || null;
};

const handleForm = (pathSegments, rawSegments) => {
  const sortedSegments = [...pathSegments].sort((a, b) => b.length - a.length);
  const matchedSegment = sortedSegments.find((seg) => getRouteTitleFor(seg));
  if (!matchedSegment) return null;

  const formIndex = pathSegments.indexOf("form");
  const truncatedUrl = "/" + rawSegments.slice(0, formIndex + 1).join("/");
  const routeNameUrl = "/" + rawSegments.slice(0, formIndex).join("/");

  const afterForm = pathSegments.slice(formIndex + 1);
  const entityParams =
    afterForm.length > 1 ? afterForm.slice(0, -1).join(" ") : afterForm[0] || "";

  return {
    routeNameMatch: getRouteTitleFor(matchedSegment),
    routeNameUrl,
    truncatedUrl,
    entityParams,
  };
};

const handleRequest = (pathSegments, rawSegments) => {
  const requestIndex = pathSegments.indexOf("request");
  if (requestIndex === -1) return null;

  const segment = pathSegments[1];
  const afterRequest = pathSegments.slice(requestIndex + 1);
  
  const requestId = afterRequest[0] || "";
  
  const requestIdUrl = "/" + rawSegments.slice(0, requestIndex + 2).join("/");
  
  const actionSegments = afterRequest.slice(1);
  let actionTitle = null;
  
  if (actionSegments.length > 0) {
    const actionSegment = actionSegments[0];
    actionTitle = getRouteTitleFor(actionSegment);
  }

  return {
    routeNameMatch: getRouteTitleFor(segment),
    routeNameUrl: "/" + rawSegments.slice(0, requestIndex).join("/"),
    entityParams: requestId,
    actionTitle,
    requestIdUrl,
  };
};

const handleReportVariant = (pathSegments, rawSegments) => {
  if (pathSegments.length < 1) return null;
  const firstSegment = pathSegments[0] || "";

  const titles = [];
  if (firstSegment.includes("role-level")) {
    titles.push({
      title: routeName.roleLevel,
      route: "/" + rawSegments[0],
      isActive: pathSegments.length === 1,
    });
  } else if (firstSegment.includes("user-level")) {
    titles.push({
      title: routeName.userLevel,
      route: "/" + rawSegments[0],
      isActive: pathSegments.length === 1,
    });
  }

  const extras =
    pathSegments.length > 1
      ? {
          variantName: routeName.variants,
          truncatedUrl: "/" + rawSegments[0],
          entityParams: pathSegments.slice(1).join(" "),
        }
      : {};

  return { titles, ...extras };
};

const handleDefault = (pathSegments) => {
  if (pathSegments.length === 0) return null;
  let segment = pathSegments[0];
  let route = "/" + segment;


  const routeTitle = getRouteTitleFor(segment);
  if (!routeTitle) return null;

  return {
    titles: [{ title: routeTitle, route, isActive: true }],
  };
};

const renderExtraTrail = ({
  routeNameMatch,
  routeNameUrl,
  entityParams,
  variantName,
  truncatedUrl,
  isReportVariant,
  actionTitle,
  requestIdUrl,
}) => {
  if (routeNameMatch && entityParams) {
    return (
      <>
        <Chevron />
        <Link to={routeNameUrl} style={{ textDecoration: "none" }}>
          <Typography component="span" className="tab-content-header-title">
            {routeNameMatch}
          </Typography>
        </Link>
        <Chevron />
        {actionTitle && requestIdUrl ? (
          <Link to={requestIdUrl} style={{ textDecoration: "none" }}>
            <Typography component="span" className="tab-content-header-title">
              {entityParams}
            </Typography>
          </Link>
        ) : (
          <Typography component="span" className="tab-content-header-title">
            {entityParams}
          </Typography>
        )}
        {actionTitle && (
          <>
            <Chevron />
            <Typography component="span" className="tab-content-header-title">
              {actionTitle}
            </Typography>
          </>
        )}
      </>
    );
  }

  if (variantName && entityParams && isReportVariant) {
    return (
      <>
        <Chevron />
        <Link to={truncatedUrl} style={{ textDecoration: "none" }}>
          <Typography component="span" className="tab-content-header-title">
            {variantName}
          </Typography>
        </Link>
        <Chevron />
        <Typography component="span" className="tab-content-header-title">
          {entityParams}
        </Typography>
      </>
    );
  }

  return null;
};

const TabContent = forwardRef(({ menuTitle }, ref) => {
  const location = useLocation();
  const rawSegments = String(location.pathname).split("/").filter(Boolean);
  const pathSegments = decodeSegments(location.pathname);

  const hasForm = pathSegments.includes("form");
  const isReportVariant = /\/(?:report|modeling)-(?:user-level|role-level)(?:\/|$)/.test(
    location.pathname
  );

  let titles = [{ title: menuTitle, route: "", isActive: false }];
  let state = {};

  if (hasForm) state = handleForm(pathSegments, rawSegments) || {};
  else if (pathSegments.includes("request")) state = handleRequest(pathSegments, rawSegments) || {};
  else if (isReportVariant) {
    const res = handleReportVariant(pathSegments, rawSegments) || {};
    titles = [...titles, ...(res.titles || [])];
    state = res;
  } else {
    const res = handleDefault(pathSegments, rawSegments) || {};
    titles = [...titles, ...(res.titles || [])];
    state = res;
  }

  const extraTrailAlreadyRenderedInside = titles.some((t) => t.isActive);
  const extraTrailNeeded = Boolean(state.routeNameMatch || (state.variantName && isReportVariant));

  return (
    <Box className="tab-content" ref={ref}>
      <Box className="tab-content-header">
        {titles.map((item, index) => (
          <React.Fragment key={`${item.title}-${index}`}>
            <Link to={item.route} style={{ textDecoration: "none" }}>
              <Typography component="span" className="tab-content-header-title">
                {item.title}
              </Typography>
            </Link>
            {item.isActive && renderExtraTrail({ ...state, isReportVariant })}
            {index !== titles.length - 1 && <Chevron />}
          </React.Fragment>
        ))}
        {!extraTrailAlreadyRenderedInside &&
          extraTrailNeeded &&
          renderExtraTrail({ ...state, isReportVariant })}
      </Box>
      <Outlet />
    </Box>
  );
});

TabContent.propTypes = {
  activeTab: PropTypes.object,
  menuTitle: PropTypes.string,
};

export default TabContent;